// /contratistas — la nómina de la organización.
//
// Es la pantalla que le da al drawer de asignación de dónde elegir: sin
// nómina no hay a quién asignarle una observación, y el enlace del
// contratista no se puede emitir. Por eso su estado vacío no es
// decorativo — es lo primero que ve un cliente nuevo el día uno, y tiene
// que decir exactamente qué falta y cómo se arregla.
//
// «Trabajos activos» sale del servidor, no se cuenta acá: se deriva del
// grafo de la receta (estados no terminales, sin el overlay de cierre) y
// una vertical con otro vocabulario cuenta bien sin tocar esta pantalla.
//
// Desde ADR-0134 facility también asigna por enlace y ve esta pantalla
// (nav «Proveedores»): el sustantivo y el objeto del enlace salen de la
// receta (`threadWords`), no están escritos para postventa.
import { useState } from "react";
import { isReadOnlyRole } from "@bitacorapro/api-types";
import { Icon } from "../components/icons";
import { QueryBoundary } from "../components/QueryBoundary";
import { Skel } from "../components/Skel";
import { useCreateProvider, useProviders, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { threadWords } from "../lib/postventa/words";

const NOMINA_HEADERS = ["Nombre", "Especialidad", "Contacto", "Trabajos activos"];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function Contratistas() {
  const providers = useProviders();
  const create = useCreateProvider();
  const { data: recipe } = useRecipe();
  const w = threadWords(recipe);
  // Read-only roles (ADR-0164) reach this page only by URL; core-api
  // answers every write with 403 ROLE_READ_ONLY, so the add controls hide.
  const canAdd = !isReadOnlyRole(useSession()?.organization.role ?? "");

  const [open, setOpen] = useState(false);
  const [shortName, setShortName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  // El servidor exige al menos una forma de contacto: un contratista sin
  // correo ni teléfono no sirve para lo único que la nómina hace, que es
  // mandarle el enlace. Se refleja acá para no pedirlo con un 400.
  const canSubmit =
    shortName.trim().length > 0 &&
    (contactEmail.trim().length > 0 || contactPhone.trim().length > 0);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        shortName: shortName.trim(),
        ...(specialty.trim() ? { specialty: specialty.trim() } : {}),
        ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
        ...(contactPhone.trim() ? { contactPhone: contactPhone.trim() } : {}),
      });
      setOpen(false);
      setShortName("");
      setSpecialty("");
      setContactEmail("");
      setContactPhone("");
    } catch (err) {
      setError(err instanceof Error ? err.message : `No pudimos guardar el ${w.assigneeLower}`);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-header__eyebrow">Gestión · Nómina de proveedores</div>
          <h1>{w.assigneePlural}</h1>
          <p className="page-header__sub">
            La nómina disponible para asignar observaciones. El {w.assigneeLower} trabaja por enlace
            público, sin cuenta: al asignarle una observación se le emite un enlace por{" "}
            {w.assetLower}.
          </p>
        </div>
        {canAdd && (
          <div className="page-header__actions">
            <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
              Agregar {w.assigneeLower}
            </button>
          </div>
        )}
      </div>

      <QueryBoundary
        query={providers}
        label="la nómina"
        skeleton={<NominaSkeleton />}
        isEmpty={(data) => data.providers.length === 0}
        empty={
          <div className="empty empty--page">
            <div className="empty__icon" aria-hidden="true">
              <Icon.Users />
            </div>
            <h2>Todavía no hay {w.assigneePluralLower}</h2>
            <p>
              Sin nómina no hay a quién asignarle una observación: el drawer de asignación queda sin
              opciones y no se puede emitir el enlace. Agrega el primero con su nombre y una forma
              de contacto.
            </p>
            {canAdd && (
              <div className="empty__actions">
                <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
                  Agregar el primero
                </button>
              </div>
            )}
          </div>
        }
      >
        {(data) => (
          <table className="dtable">
            <thead>
              <tr>
                {NOMINA_HEADERS.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-main">
                      <span className="avatar" aria-hidden="true">
                        {initials(p.shortName)}
                      </span>
                      <span>
                        <span className="cell-title">{p.shortName}</span>
                        {p.legalName && <span className="cell-sub">{p.legalName}</span>}
                      </span>
                    </div>
                  </td>
                  <td>{p.specialty ?? "—"}</td>
                  <td>{p.contactEmail ?? p.contactPhone ?? "—"}</td>
                  <td>{p.activeJobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </QueryBoundary>

      {open && (
        <>
          <div className="drawer-backdrop" onClick={() => setOpen(false)} />
          <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="nuevo-prov">
            <div className="drawer__head">
              <h2 className="drawer__title" id="nuevo-prov">
                Agregar {w.assigneeLower}
              </h2>
              <button
                type="button"
                className="drawer__close"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
              >
                <Icon.Close />
              </button>
            </div>
            <div className="drawer__body">
              <div className="drawer__section-label">
                <label htmlFor="prov-nombre">Nombre o empresa</label>
              </div>
              <input
                id="prov-nombre"
                className="input"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                placeholder="Fuentes Gasfitería SpA"
              />

              <div className="drawer__section-label">
                <label htmlFor="prov-esp">Especialidad</label>
              </div>
              <input
                id="prov-esp"
                className="input"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="Gasfitería"
              />

              <div className="drawer__section-label">
                <label htmlFor="prov-mail">Correo</label>
              </div>
              <input
                id="prov-mail"
                className="input"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contacto@empresa.cl"
              />

              <div className="drawer__section-label">
                <label htmlFor="prov-tel">Teléfono</label>
              </div>
              <input
                id="prov-tel"
                className="input"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+56 9 8123 4567"
              />

              <p className="cell-sub">
                Basta con un correo o un teléfono: es por donde le vas a mandar el enlace.
              </p>

              {error && (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              )}
            </div>
            <div className="drawer__foot">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setOpen(false)}
                disabled={create.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void submit()}
                disabled={!canSubmit || create.isPending}
              >
                {create.isPending ? "Guardando…" : "Agregar"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// The same bare `.dtable` the nómina lands in, static headers for real,
// with the avatar + name pair in the first column: enough rows to fill a
// 1280×800 viewport under the header.
function NominaSkeleton() {
  return (
    <table className="dtable">
      <thead>
        <tr>
          {NOMINA_HEADERS.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <tr key={i}>
            <td>
              <div className="cell-main">
                <Skel variant="circle" width={28} />
                <Skel width={i % 2 ? 140 : 180} height={12} />
              </div>
            </td>
            <td>
              <Skel width={90} height={12} />
            </td>
            <td>
              <Skel width={150} height={12} />
            </td>
            <td>
              <Skel width={24} height={12} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
